import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, BookOpen, CheckSquare, Star, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import InviteParents from '../components/InviteParents';
import CoachRosterLeaderboard from '../components/CoachRosterLeaderboard';
import CoachWelcome from '../components/CoachWelcome';
import WhatsAppConfig from '../components/WhatsAppConfig';
import CoachAnalytics from '../components/CoachAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachDashboard() {
  const navigate = useNavigate();
  const { getAuthHeader, logout, user } = useAuth();
  const { theme } = useTheme();
  const [students, setStudents] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [publicFeedback, setPublicFeedback] = useState('');
  const [showWaba, setShowWaba] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [privateNotes, setPrivateNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Redirect non-coaches to player dashboard. Accept both v1
    // ("Coach", title-case from legacy Mongo rows) and v2 ("coach",
    // lower-case from the identity_router) — the v2 path lowercases
    // every role on the account doc.
    const role = (user?.cricket_role || '').toLowerCase();
    if (user && role && role !== 'coach') {
      navigate('/player-dashboard', { replace: true });
      return;
    }

    fetchStudents();
    fetchReviewQueue();
    fetchPendingRequests();
  }, [user]);

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${API}/coach/pending-requests`, {
        headers: getAuthHeader()
      });
      setPendingRequests(response.data.pending || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/coach/students`, {
        headers: getAuthHeader()
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchReviewQueue = async () => {
    try {
      const response = await axios.get(`${API}/coach/review-queue`, {
        headers: getAuthHeader()
      });
      setReviewQueue(response.data.pending_reviews || []);
    } catch (error) {
      console.error('Error fetching review queue:', error);
    }
  };

  const submitReview = async (shareId) => {
    if (!publicFeedback.trim()) {
      toast.error('Please provide public feedback');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/coach/review-journal/${shareId}`, {
        public_feedback: publicFeedback,
        private_notes: privateNotes
      }, {
        headers: getAuthHeader()
      });

      toast.success('Review submitted!');
      setSelectedReview(null);
      setPublicFeedback('');
      setPrivateNotes('');
      fetchReviewQueue();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const acceptStudent = async (relationshipId) => {
    try {
      await axios.post(`${API}/coach/accept-student/${relationshipId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Student accepted!');
      fetchPendingRequests();
      fetchStudents();
    } catch (error) {
      toast.error('Failed to accept student');
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Coach Dashboard</h1>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Manage students and provide feedback</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')}>Home</Button>
              <Button variant="outline" onClick={() => { logout(); navigate('/'); }} className="text-red-500 border-red-500 hover:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="py-6">
              <Users className="w-10 h-10 mx-auto mb-3 text-blue-400" />
              <div className="text-4xl font-bold gradient-text mb-1">{students.length}</div>
              <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Students</div>
              {pendingRequests.length > 0 && (
                <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`}>
                  {pendingRequests.length} pending
                </div>
              )}
            </div>
          </Card>

          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="py-6">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-purple-400" />
              <div className="text-4xl font-bold gradient-text mb-1">{reviewQueue.length}</div>
              <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Pending Reviews</div>
            </div>
          </Card>

          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="py-6">
              <Star className="w-10 h-10 mx-auto mb-3 text-yellow-400" />
              <div className="text-4xl font-bold gradient-text mb-1">4.8</div>
              <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Rating</div>
            </div>
          </Card>
        </div>

        {/* Coach Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card 
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => {
              // Scroll to review queue
              document.getElementById('review-queue')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Review Journals</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{reviewQueue.length} pending</p>
            </div>
          </Card>

          <Card
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => setShowInvite(true)}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
                <span className="text-2xl">💬</span>
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Invite parents</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>One SMS each</p>
            </div>
          </Card>

          <Card
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => setShowWaba(true)}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <span className="text-2xl">📣</span>
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>WhatsApp blast</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Send match cards</p>
            </div>
          </Card>

          <Card
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => navigate('/drills')}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <CheckSquare className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Assign Tasks</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Drill library</p>
            </div>
          </Card>

          <Card 
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => toast.info('Student roster coming in Phase 2!')}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>My Students</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{students.length} active</p>
            </div>
          </Card>

          <Card 
            className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
            onClick={() => toast.info('Review history coming in Phase 2!')}
          >
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                <Star className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Reviews</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>View feedback</p>
            </div>
          </Card>
        </div>

        {/* Pending Connection Requests */}
        {pendingRequests.length > 0 && (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Pending Connection Requests</h2>
            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div key={request.id} className={`p-4 rounded-lg flex items-center justify-between ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <div>
                    <p className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{request.student_name}</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Requested: {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => acceptStudent(request.id)} className="bg-gradient-to-r from-green-500 to-emerald-600">
                      Accept
                    </Button>
                    <Button size="sm" variant="outline">
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Review Queue */}
        <Card id="review-queue" className={`glass-card mt-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Pending Journal Reviews</h2>
          
          {reviewQueue.length > 0 ? (
            <div className="space-y-4">
              {reviewQueue.map((share) => (
                <div key={share.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Journal: {share.journal_id.substring(0, 8)}</span>
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                      {new Date(share.shared_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {selectedReview === share.id ? (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Public Feedback (Student Will See)
                        </label>
                        <Textarea
                          value={publicFeedback}
                          onChange={(e) => setPublicFeedback(e.target.value)}
                          rows={4}
                          placeholder="Great session! Keep focusing on..."
                          className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}
                        />
                      </div>
                      
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Private Notes (Coach-Only, Invisible to Student)
                        </label>
                        <Textarea
                          value={privateNotes}
                          onChange={(e) => setPrivateNotes(e.target.value)}
                          rows={3}
                          placeholder="Internal notes about student progress..."
                          className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => submitReview(share.id)}
                          disabled={submitting}
                          className="bg-gradient-to-r from-green-500 to-emerald-600"
                        >
                          {submitting ? 'Submitting...' : 'Submit Review'}
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedReview(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setSelectedReview(share.id)}>
                      Review Journal
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No pending reviews
            </p>
          )}
        </Card>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 space-y-5">
        <CoachRosterLeaderboard theme={theme} />
        <CoachAnalytics theme={theme} />
      </div>

      {showInvite && (
        <InviteParents onClose={() => setShowInvite(false)} theme={theme} />
      )}
      {showWaba && (
        <WhatsAppConfig onClose={() => setShowWaba(false)} theme={theme} />
      )}

      <CoachWelcome />
    </div>
  );
}
